// src/pages/Vacantes.jsx
import { useState, useEffect } from 'react';
import { getSchedulesRecent, saveSchedule, deleteSchedule, getEmployees, getPosts, getAppointments } from '../data/store';

function fmt(d) {
if (typeof d === 'string') return d;
return String(d.getDate()).padStart(2,'0') + '/' + String(d.getMonth()+1).padStart(2,'0') + '/' + d.getFullYear();
}
function getBisemana() {
const today = new Date();
const day = today.getDay();
const monday = new Date(today);
monday.setDate(today.getDate() - (day === 0 ? 6 : day - 1));
return Array.from({length:14}, (_,i) => { const d = new Date(monday); d.setDate(monday.getDate() + i); return d; });
}
// Siempre muestra minutos: "10:00 PM - 6:00 AM"
function formatShift12h(shift) {
if (!shift) return '';
if (shift.includes('AM') || shift.includes('PM')) return shift.replace('/', ' - ');
let start = '', end = '';
if (shift.includes('/')) { [start, end] = shift.split('/'); }
else if (shift.includes('-')) { [start, end] = shift.split('-'); }
else return shift;
const convert = (t) => {
if (!t) return '';
let [h, m] = t.split(':');
h = parseInt(h, 10);
m = m ? String(m).padStart(2,'0') : '00';
const ampm = h >= 12 ? 'PM' : 'AM';
h = h % 12 || 12;
return h + ':' + m + ' ' + ampm;
};
return convert(start) + ' - ' + convert(end);
}

function normalizeShift(shift) {
if (!shift) return '';
let clean = shift.toString().trim().replace(/\s+/g, '').replace('-', '/').toUpperCase();
const to24 = (time) => {
const match = time.match(/^(\d{1,2})(?::(\d{2}))?(AM|PM)?$/);
if (!match) return time;
let h = parseInt(match[1], 10);
const m = match[2] || '00';
const ampm = match[3];
if (ampm === 'PM' && h !== 12) h += 12;
if (ampm === 'AM' && h === 12) h = 0;
return String(h).padStart(2, '0') + ':' + m;
};
const parts = clean.split('/');
if (parts.length !== 2) return clean;
return to24(parts[0]) + '/' + to24(parts[1]);
}

function shiftToMinutesRange(shift) {
const normalized = normalizeShift(shift);
const parts = normalized.includes('/') ? normalized.split('/') : normalized.split('-');
if (parts.length !== 2) return null;
const toMin = (t) => { const [h, m] = t.split(':').map(Number); return h * 60 + (m || 0); };
let start = toMin(parts[0]);
let end = toMin(parts[1]);
if (end <= start) end += 1440;
return { start, end };
}

function overlap(shiftA, shiftB) {
const a = shiftToMinutesRange(shiftA);
const b = shiftToMinutesRange(shiftB);
if (!a || !b) return false;
const overlapsSameDay = Math.max(a.start, b.start) < Math.min(a.end, b.end);
const overlapsNextDay = Math.max(a.start, b.start + 1440) < Math.min(a.end, b.end + 1440);
return overlapsSameDay || overlapsNextDay;
}


// Verifica si un conjunto de turnos cubre las 24 horas sin huecos
// Maneja turnos overnight correctamente (cobertura circular)
function findCoverageGaps(shifts) {
if (!shifts || shifts.length === 0) return [{ start: 0, end: 1440 }];

// Convertir a rangos de minutos (sin extender overnight - manejar circular)
const ranges = [];
shifts.forEach(s => {
  const normalized = normalizeShift(s);
  const parts = normalized.includes('/') ? normalized.split('/') : normalized.split('-');
  if (parts.length !== 2) return;
  const toMin = (t) => { const [h, m] = t.split(':').map(Number); return h * 60 + (m || 0); };
  let start = toMin(parts[0]);
  let end = toMin(parts[1]);
  if (end <= start) {
    // Turno overnight: dividir en dos segmentos
    ranges.push({ start: start, end: 1440 });
    ranges.push({ start: 0, end: end });
  } else {
    ranges.push({ start, end });
  }
});

if (ranges.length === 0) return [{ start: 0, end: 1440 }];

// Ordenar por inicio
ranges.sort((a, b) => a.start - b.start);

const gaps = [];
let covered = 0;
for (const r of ranges) {
  if (r.start > covered) {
    gaps.push({ start: covered, end: r.start });
  }
  covered = Math.max(covered, r.end);
}
if (covered < 1440) gaps.push({ start: covered, end: 1440 });
return gaps;
}

function minutesToTime(min) {
const h = Math.floor(min / 60) % 24;
const m = min % 60;
const ampm = h >= 12 ? 'PM' : 'AM';
const h12 = h % 12 || 12;
return h12 + ':' + String(m).padStart(2, '0') + ' ' + ampm;
}

// Detecta turnos duplicados (mismo shift exacto en el mismo puesto/día)
function findDuplicateShifts(assignedSchedules) {
const seen = {};
const duplicates = [];
assignedSchedules.forEach(s => {
const key = normalizeShift(s.shift);
if (seen[key]) {
duplicates.push(key);
} else {
seen[key] = true;
}
});
return [...new Set(duplicates)];
}

export default function Vacantes() {
const [posts, setPosts] = useState([]);
const [schedules, setSchedules] = useState([]);
const [employees, setEmployees] = useState([]);
const [appointments, setAppointments] = useState([]);
const [selectedVacancy, setSelectedVacancy] = useState(null);
const [specialChange, setSpecialChange] = useState(null);
const [loading, setLoading] = useState(true);
const days = getBisemana();

const reload = async () => {
try {
const [p, s, e, a] = await Promise.all([getPosts(), getSchedulesRecent(), getEmployees(), getAppointments()]);
setPosts(p || []); setSchedules(s || []); setEmployees(e || []); setAppointments(a || []);
} catch (err) { console.error('Error cargando vacantes:', err); }
finally { setLoading(false); }
};
useEffect(() => { reload(); }, []);

const vacancies = [];
posts.forEach(post => {
const guardsNeeded = Number(post.required_guards || post.guards_per_shift || post.guardsPerShift || 1);
const coverage = post.coverage || '24/7';
const activeDays = post.days || [0,1,2,3,4,5,6];
const shiftByDay = post.shift_by_day || null;

days.forEach(day => {
const dow = day.getDay();
const dateStr = fmt(day);

let shiftsForDay = [];
if (shiftByDay) {
shiftsForDay = shiftByDay[String(dow)] || [];
} else {
if (coverage === 'weekly' && !activeDays.includes(dow)) return;
shiftsForDay = post.shifts || [];
}
if (shiftsForDay.length === 0) return;

if (coverage === '24/7') {
// LOGICA 24/7: verificar que las 24 horas estén cubiertas
const assignedToday = schedules.filter(s => {
const sPostId = s.post_id || s.postId;
return String(sPostId) === String(post.id) &&
String(s.date).trim() === String(dateStr).trim();
});

// Detectar turnos duplicados
const duplicates = findDuplicateShifts(assignedToday);

// Verificar cobertura de 24 horas
const assignedShifts = assignedToday.map(s => s.shift).filter(Boolean);
const gaps = findCoverageGaps(assignedShifts);

// Cada hueco es una vacante
gaps.forEach((gap, idx) => {
// Intentar asignar al shift más cercano del post para cubrir ese hueco
const gapMid = (gap.start + gap.end) / 2;
const bestShift = shiftsForDay.reduce((best, sh) => {
const r = shiftToMinutesRange(sh);
if (!r) return best;
const dist = Math.abs((r.start + r.end) / 2 - gapMid);
if (!best || dist < best.dist) return { sh, dist };
return best;
}, null);

vacancies.push({
key: post.id + '_' + dateStr + '_gap_' + idx,
post,
date: dateStr,
day,
shift: bestShift ? bestShift.sh : shiftsForDay[0],
guardsNeeded,
guardsAssigned: assignedToday.length,
slotIndex: idx,
isGap: true,
gapStart: gap.start,
gapEnd: gap.end,
duplicates,
});
});

// También reportar turnos duplicados como advertencia
duplicates.forEach((dupShift, idx2) => {
// Solo si no ya se creó una vacante por ese gap
vacancies.push({
key: post.id + '_' + dateStr + '_dup_' + idx2,
post,
date: dateStr,
day,
shift: dupShift,
guardsNeeded,
guardsAssigned: assignedToday.length,
slotIndex: idx2,
isDuplicate: true,
duplicates: [dupShift],
});
});

} else {
// LOGICA días específicos: misma lógica anterior por turno
shiftsForDay.forEach(shift => {
const assigned = schedules.filter(s => {
const sPostId = s.post_id || s.postId;
return String(sPostId) === String(post.id) &&
String(s.date).trim() === String(dateStr).trim() &&
overlap(normalizeShift(s.shift), normalizeShift(shift));
});
const missing = guardsNeeded - assigned.length;
if (missing > 0) {
for (let i = 0; i < missing; i++) {
vacancies.push({ key: post.id + '_' + dateStr + '_' + shift + '_' + i, post, date: dateStr, day, shift, guardsNeeded, guardsAssigned: assigned.length, slotIndex: i });
}
}
});
}
});
});

const getAvailable = (vacancy) => {
const recommended = [];
const available = [];
const limited = [];
const vacancyDate = new Date(vacancy.date.split('/').reverse().join('-') + 'T12:00:00');

const getShiftInfo = (emp) => {
const empAllSchedules = schedules
.filter(s => String(s.employee_id || s.employeeId) === String(emp.id))
.map(s => ({ ...s, realDate: new Date(s.date.split('/').reverse().join('-') + 'T12:00:00') }))
.sort((a, b) => a.realDate - b.realDate);
const last = [...empAllSchedules].filter(s => s.realDate < vacancyDate).pop();
const next = empAllSchedules.find(s => s.realDate > vacancyDate);
return {
lastShift: last ? last.date + ' · ' + formatShift12h(last.shift) + ' · ' + (last.post_name || '') : 'Sin turno previo',
nextShift: next ? next.date + ' · ' + formatShift12h(next.shift) + ' · ' + (next.post_name || '') : 'Sin próximo turno'
};
};

employees.forEach(emp => {
const empSchedulesToday = schedules.filter(s => String(s.employee_id || s.employeeId) === String(emp.id) && s.date === vacancy.date);
const hasCita = appointments.some(a => String(a.employee_id || a.employeeId) === String(emp.id) && a.date === vacancy.date);
const info = getShiftInfo(emp);
if (hasCita) { limited.push({ emp, reason: 'Tiene cita ese día', ...info }); return; }
let conflict = false; let reason = '';
for (let s of empSchedulesToday) {
if (overlap(normalizeShift(s.shift), normalizeShift(vacancy.shift))) {
conflict = true; reason = 'Trabaja ' + formatShift12h(s.shift) + ' en ' + s.post_name; break;
}
}
if (conflict) { limited.push({ emp, reason, ...info }); return; }
const hasWorkedThisPost = schedules.some(s => String(s.employee_id || s.employeeId) === String(emp.id) && String(s.post_id || s.postId) === String(vacancy.post.id));
if (hasWorkedThisPost) { recommended.push({ emp, ...info }); } else { available.push({ emp, ...info }); }
});
return { recommended, available, limited };
};

const openSpecialChange = (vacancy, emp) => {
const empShift = schedules.find(s => String(s.employee_id || s.employeeId) === String(emp.id) && s.date === vacancy.date && overlap(normalizeShift(s.shift), normalizeShift(vacancy.shift)));
const replacementVacancy = empShift ? { key: 'replacement_' + empShift.id, post: { id: empShift.post_id || empShift.postId, name: empShift.post_name || empShift.postName }, date: empShift.date, shift: empShift.shift, guardsNeeded: 1, guardsAssigned: 0 } : null;
setSpecialChange({ vacancy, emp, empShift, replacementVacancy });
};

const handleAssign = async (vacancy, emp) => {
const turnosHoy = schedules.filter(s => String(s.employee_id || s.employeeId) === String(emp.id) && s.date === vacancy.date);
if (turnosHoy.length > 0) {
const calcHours = (shift) => {
if (!shift) return 0;
const parts = shift.includes('/') ? shift.split('/') : shift.split('-');
if (parts.length < 2) return 0;
const toMin = (t) => { const [h, m] = t.split(':').map(Number); return h * 60 + (m || 0); };
let diff = toMin(parts[1]) - toMin(parts[0]);
if (diff < 0) diff += 24 * 60;
return diff / 60;
};
const horasExistentes = turnosHoy.reduce((acc, s) => acc + calcHours(s.shift), 0);
const horasNuevas = calcHours(vacancy.shift);
const totalHoras = horasExistentes + horasNuevas;
if (totalHoras >= 12) {
const ok = window.confirm('⚠️ AVISO: ' + emp.name + ' va a trabajar ' + totalHoras + ' horas este día.\nTurno existente + este turno = ' + totalHoras + 'h.\n\n¿Confirmar turno extendido de ' + totalHoras + ' horas?');
if (!ok) return;
} else {
const ok = window.confirm(emp.name + ' ya tiene un turno asignado el ' + vacancy.date + '.\nTotal con este turno: ' + totalHoras + 'h.\n\n¿Agregar turno adicional?');
if (!ok) return;
}
}
await saveSchedule({ id: Date.now().toString(), employee_id: emp.id, post_id: vacancy.post.id, post_name: vacancy.post.name, date: vacancy.date, shift: normalizeShift(vacancy.shift) });
setSelectedVacancy(null);
await reload();
};

// Agrupar vacantes: separar duplicados de verdaderos huecos
const grouped = {};
vacancies.filter(v => !v.isDuplicate).forEach(v => { if (!grouped[v.post.name]) grouped[v.post.name] = []; grouped[v.post.name].push(v); });
const duplicateAlerts = vacancies.filter(v => v.isDuplicate);

if (loading) return <div style={{ color:'#fff', padding:'2rem' }}>Cargando vacantes...</div>;

return (
<div style={{ color:'#fff' }}>
<div style={{ marginBottom: 24 }}>
<h1 style={{ margin: 0, fontSize: 26, fontWeight: 700, color: '#F5C518' }}>Gestión de Vacantes</h1>
<p style={{ margin: '4px 0 0', color: '#888', fontSize: 14 }}>
{vacancies.filter(v=>!v.isDuplicate).length} vacante(s) en la bisemana — {fmt(days[0])} al {fmt(days[13])}
</p>
</div>

{/* Alertas de turnos duplicados */}
{duplicateAlerts.length > 0 && (
<div style={{ background: 'rgba(251,191,36,0.1)', border: '1.5px solid rgba(251,191,36,0.4)', borderRadius: 12, padding: '14px 20px', marginBottom: 20 }}>
<div style={{ color: '#fbbf24', fontWeight: 700, fontSize: 14, marginBottom: 8 }}>⚠️ Turnos duplicados detectados ({duplicateAlerts.length})</div>
{duplicateAlerts.map(v => (
<div key={v.key} style={{ color: '#aaa', fontSize: 13, marginBottom: 4 }}>
• {v.post.name} — {v.date}: turno <strong style={{ color:'#fbbf24' }}>{formatShift12h(v.shift)}</strong> está asignado más de una vez
</div>
))}
</div>
)}

{vacancies.filter(v=>!v.isDuplicate).length === 0 ? (
<div style={{ background: '#1a1a1a', border:'1px solid #222', borderRadius: 14, padding: '40px', textAlign: 'center' }}>
<div style={{ fontSize: 48, marginBottom: 12 }}>✓</div>
<h2 style={{ color: '#34d399', margin: 0 }}>Sin vacantes</h2>
<p style={{ color: '#888', marginTop: 8 }}>Todos los turnos están cubiertos esta bisemana.</p>
</div>
) : (
<div style={{ display: 'grid', gridTemplateColumns: selectedVacancy ? '1fr 1fr' : '1fr', gap: 20 }}>
<div>
{Object.entries(grouped).map(([postName, pvacs]) => (
<div key={postName} style={{ background: '#1a1a1a', border:'1px solid #222', borderRadius: 14, marginBottom: 16, overflow: 'hidden' }}>
<div style={{ background: '#0d0d0d', padding: '12px 20px', display:'flex', justifyContent:'space-between', alignItems:'center' }}>
<h3 style={{ margin: 0, color: '#F5C518', fontSize: 15, fontWeight: 700 }}>{postName}</h3>
<span style={{ color: '#f87171', fontSize: 12, fontWeight:600 }}>{pvacs.length} vacante(s)</span>
</div>
{pvacs.map(v => (
<div key={v.key} onClick={() => setSelectedVacancy(selectedVacancy?.key === v.key ? null : v)}
style={{ padding: '14px 20px', borderBottom: '1px solid #222', cursor: 'pointer', display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: selectedVacancy?.key === v.key ? 'rgba(248,113,113,0.08)' : 'transparent' }}>
<div>
<div style={{ fontWeight: 600, color: '#fff', fontSize: 14 }}>
{new Date(v.date.split('/').reverse().join('-') + 'T12:00:00').toLocaleDateString('es-PR', { weekday: 'long', month: 'long', day: 'numeric' })}
</div>
<div style={{ fontSize: 12, color: '#888', marginTop: 2 }}>
{v.isGap ? (
<>Hueco sin cubrir: <strong style={{color:'#f87171'}}>{minutesToTime(v.gapStart)}</strong> → <strong style={{color:'#f87171'}}>{minutesToTime(v.gapEnd % 1440)}</strong></>
) : (
<>Turno: <strong style={{color:'#93c5fd'}}>{formatShift12h(v.shift)}</strong></>
)} · {v.guardsAssigned}/{v.guardsNeeded} guardias
</div>
</div>
<span style={{ background: v.isGap ? 'rgba(239,68,68,0.2)' : 'rgba(248,113,113,0.15)', color: '#f87171', border:'1px solid rgba(248,113,113,0.3)', fontSize: 11, fontWeight: 700, padding: '3px 8px', borderRadius: 5 }}>
{v.isGap ? 'HUECO 24/7' : 'VACANTE'}
</span>
</div>
))}
</div>
))}
</div>

{selectedVacancy && (
<div style={{ position:'sticky', top:24 }}>
<div style={{ background: '#1a1a1a', border:'1px solid #333', borderRadius: 14, overflow: 'hidden' }}>
<div style={{ background: '#0d0d0d', padding: '16px 20px', display:'flex', justifyContent:'space-between', alignItems:'center' }}>
<div>
<h3 style={{ margin: 0, color: '#F5C518', fontSize: 15, fontWeight: 700 }}>Empleados Disponibles</h3>
<p style={{ margin: '2px 0 0', color: '#888', fontSize: 12 }}>{selectedVacancy.post.name} · {formatShift12h(selectedVacancy.shift)} · {selectedVacancy.date}</p>
</div>
<button onClick={() => setSelectedVacancy(null)} style={{ background: 'rgba(255,255,255,0.1)', border: 'none', color: 'white', width: 28, height: 28, borderRadius: '50%', cursor: 'pointer', fontSize: 16 }}>×</button>
</div>
<div style={{ padding: '12px 0' }}>
{(() => {
const { recommended, available, limited } = getAvailable(selectedVacancy);
if (recommended.length === 0 && available.length === 0 && limited.length === 0) {
return (<div style={{ padding: '24px', textAlign: 'center', color: '#888' }}><div style={{ fontSize: 32, marginBottom: 8 }}>😔</div>No hay empleados disponibles para este turno.</div>);
}
return (
<>
{recommended.length > 0 && <div style={{ padding: '10px 20px', color: '#F5C518', fontSize: 12, fontWeight: 700 }}>⭐ Recomendados — ya han trabajado este puesto</div>}
{recommended.map(({ emp, lastShift, nextShift }) => (
<div key={emp.id + '_rec'} style={{ padding: '12px 20px', borderBottom: '1px solid #222', display: 'flex', alignItems: 'center', gap: 12, background: 'rgba(245,197,24,0.06)' }}>
<div style={{ width: 40, height: 40, borderRadius: 10, background: '#2a2a2a', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, fontWeight: 700, color: '#F5C518' }}>{emp.name.split(' ').map(n => n[0]).join('').slice(0,2)}</div>
<div style={{ flex: 1 }}>
<div style={{ fontWeight: 600 }}>{emp.name}</div>
<div style={{ fontSize: 11, color: '#888' }}>{'\u00daltimo: ' + lastShift}</div>
<div style={{ fontSize: 11, color: '#888' }}>{'Pr\u00f3ximo: ' + nextShift}</div>
</div>
<div style={{ display: 'flex', gap: 6 }}>
<button onClick={() => handleAssign(selectedVacancy, emp)} style={{ padding: '7px 14px', borderRadius: 7, background: '#F5C518', color: '#000', fontWeight: 700, cursor: 'pointer' }}>Asignar</button>
<button onClick={() => openSpecialChange(selectedVacancy, emp)} style={{ padding: '7px 14px', borderRadius: 7, background: '#fbbf24', color: '#000', fontWeight: 700, cursor: 'pointer', border: 'none' }}>Cambio especial</button>
</div>
</div>
))}
{available.length > 0 && <div style={{ padding: '10px 20px', color: '#34d399', fontSize: 12, fontWeight: 700 }}>🟢 Disponibles</div>}
{available.map(({ emp, lastShift, nextShift }) => (
<div key={emp.id + '_avail'} style={{ padding: '12px 20px', borderBottom: '1px solid #222', display: 'flex', alignItems: 'center', gap: 12 }}>
<div style={{ width: 40, height: 40, borderRadius: 10, background: '#2a2a2a', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, fontWeight: 700, color: '#34d399' }}>{emp.name.split(' ').map(n => n[0]).join('').slice(0,2)}</div>
<div style={{ flex: 1 }}>
<div style={{ fontWeight: 600 }}>{emp.name}</div>
<div style={{ fontSize: 11, color: '#888' }}>{'\u00daltimo: ' + lastShift}</div>
<div style={{ fontSize: 11, color: '#888' }}>{'Pr\u00f3ximo: ' + nextShift}</div>
<div style={{ fontSize: 11, color: '#888' }}>{emp.type === 'full-time' ? 'Full-Time' : 'Part-Time'}</div>
</div>
<button onClick={() => handleAssign(selectedVacancy, emp)} style={{ padding: '7px 14px', borderRadius: 7, background: '#34d399', color: '#000', fontWeight: 700, cursor: 'pointer' }}>Asignar</button>
</div>
))}
{limited.length > 0 && (
<>
<div style={{ padding: '10px 20px', color: '#fbbf24', fontSize: 12, fontWeight: 700 }}>⚠️ Con conflictos / limitaciones</div>
{limited.map(({ emp, reason, lastShift, nextShift }) => (
<div key={emp.id + '_limit'} style={{ padding: '12px 20px', borderBottom: '1px solid #222', opacity: 0.6 }}>
<div style={{ fontWeight: 600 }}>{emp.name}</div>
<div style={{ fontSize: 11, color: '#888' }}>{'\u00daltimo: ' + lastShift}</div>
<div style={{ fontSize: 11, color: '#888' }}>{'Pr\u00f3ximo: ' + nextShift}</div>
<div style={{ fontSize: 11, color: '#f87171' }}>{reason}</div>
</div>
))}
</>
)}
</>
);
})()}
</div>
</div>
</div>
)}
</div>
)}

{specialChange && (
<div style={{ position: 'fixed', top: 0, left: 0, width: '100%', height: '100%', background: 'rgba(0,0,0,0.7)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999 }}>
<div style={{ background: '#1a1a1a', padding: 24, borderRadius: 12, width: 400 }}>
<h3 style={{ color: '#F5C518' }}>Cambio especial</h3>
<p>{specialChange.emp.name} será movido a:<br /><strong>{formatShift12h(specialChange.vacancy.shift)} en {specialChange.vacancy.post.name}</strong></p>
{specialChange.empShift ? (
<>
<p style={{ color: '#f87171' }}>⚠️ Dejará vacante:<br />{formatShift12h(specialChange.empShift.shift)} en {specialChange.empShift.post_name}</p>
{specialChange.replacementVacancy && (
<div style={{ marginTop: 15 }}>
<div style={{ color: '#F5C518', fontSize: 12, fontWeight: 700 }}>Sugerencias para cubrir ese turno:</div>
{getAvailable(specialChange.replacementVacancy).recommended.slice(0,3).map(({ emp }) => (
<div key={emp.id} style={{ fontSize: 12, marginTop: 6, color: '#34d399' }}>• {emp.name}</div>
))}
</div>
)}
</>
) : (
<p style={{ color: '#34d399' }}>✔ Este empleado está libre ese día.</p>
)}
<div style={{ display: 'flex', gap: 10, marginTop: 20 }}>
<button onClick={async () => {
if (specialChange.empShift) { await deleteSchedule(specialChange.empShift.id); }
await saveSchedule({ id: Date.now().toString(), employee_id: specialChange.emp.id, post_id: specialChange.vacancy.post.id, post_name: specialChange.vacancy.post.name, date: specialChange.vacancy.date, shift: normalizeShift(specialChange.vacancy.shift) });
setSpecialChange(null); await reload();
}} style={{ flex: 1, background: '#34d399', padding: 10, borderRadius: 6, fontWeight: 700 }}>Confirmar</button>
<button onClick={() => setSpecialChange(null)} style={{ flex: 1, background: '#444', padding: 10, borderRadius: 6 }}>Cancelar</button>
</div>
</div>
</div>
)}
</div>
);
}
